import { PublicKey } from '@solana/web3.js';
import { PROGRAM_ID } from '../config';
import BN from 'bn.js';

// Seed constants (matching the Solana program)
const SEEDS = {
  CONFIG: 'config',
  BOSS_CONFIG: 'boss_config',
  BOSS_MINT: 'boss_mint',
  GAME_TYPE: 'game_type',
  PLAYER: 'player',
  GAME_HISTORY: 'game_history',
  STAKE_POOL: 'stake_pool',
  BOSS_VAULT: 'boss_vault',
  MARKET_CONFIG: 'market_config',
  MARKET_LOT: 'market_lot',
  MARKET_VAULT: 'market_vault',
  YEAR_MINT: 'year_mint',
  CRIME_MINT: 'crime_mint',
  RESPECT_MINT: 'respect_mint',
  BRIBE_MINT: 'bribe_mint',
  GRUNT_MINT: 'grunt_mint',
};

// Get global config PDA
export function getConfigPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.CONFIG)],
    programId
  );
}

// Get boss token config PDA
export function getBossConfigPda(
  tier: number,
  category: number,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.BOSS_CONFIG),
      Buffer.from([tier]),
      Buffer.from([category]),
    ],
    programId
  );
}

// Get boss token mint PDA
export function getBossMintPda(
  tier: number,
  category: number,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.BOSS_MINT),
      Buffer.from([tier]),
      Buffer.from([category]),
    ],
    programId
  );
}

// Get game type config PDA
export function getGameTypePda(
  index: number,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.GAME_TYPE),
      Buffer.from([index]),
    ],
    programId
  );
}

// Get player state PDA
export function getPlayerStatePda(
  player: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.PLAYER),
      player.toBuffer(),
    ],
    programId
  );
}

// Get game history PDA
export function getGameHistoryPda(
  player: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.GAME_HISTORY),
      player.toBuffer(),
    ],
    programId
  );
}

// Get stake pool PDA
export function getStakePoolPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.STAKE_POOL)],
    programId
  );
}

// Get boss vault PDA (for staking)
export function getBossVaultPda(
  bossMint: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.BOSS_VAULT),
      bossMint.toBuffer(),
    ],
    programId
  );
}

// Get market config PDA
export function getMarketConfigPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.MARKET_CONFIG)],
    programId
  );
}

// Get market lot PDA
export function getMarketLotPda(
  lotId: BN | number,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  const lotIdBN = typeof lotId === 'number' ? new BN(lotId) : lotId;
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.MARKET_LOT),
      lotIdBN.toArrayLike(Buffer, 'le', 8),
    ],
    programId
  );
}

// Get market vault PDA (for boss tokens)
export function getMarketVaultPda(
  bossMint: PublicKey,
  programId: PublicKey = PROGRAM_ID
): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [
      Buffer.from(SEEDS.MARKET_VAULT),
      bossMint.toBuffer(),
    ],
    programId
  );
}

// Get Year mint PDA
export function getYearMintPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.YEAR_MINT)],
    programId
  );
}

// Get Crime mint PDA
export function getCrimeMintPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.CRIME_MINT)],
    programId
  );
}

// Get Respect mint PDA
export function getRespectMintPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.RESPECT_MINT)],
    programId
  );
}

// Get Bribe mint PDA
export function getBribeMintPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.BRIBE_MINT)],
    programId
  );
}

// Get Grunt mint PDA
export function getGruntMintPda(programId: PublicKey = PROGRAM_ID): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from(SEEDS.GRUNT_MINT)],
    programId
  );
}

// Helper to get all token mint PDAs
export function getAllTokenMints(programId: PublicKey = PROGRAM_ID) {
  return {
    year: getYearMintPda(programId)[0],
    crime: getCrimeMintPda(programId)[0],
    respect: getRespectMintPda(programId)[0],
    bribe: getBribeMintPda(programId)[0],
    grunt: getGruntMintPda(programId)[0],
  };
}

// Helper to get boss config and mint PDAs for a tier/category
export function getBossTokenPdas(tier: number, category: number, programId: PublicKey = PROGRAM_ID) {
  return {
    config: getBossConfigPda(tier, category, programId)[0],
    mint: getBossMintPda(tier, category, programId)[0],
    vault: getBossVaultPda(getBossMintPda(tier, category, programId)[0], programId)[0],
  };
}
